import { useState } from "react";
import { IconNext, IconPause, IconPlay, IconPrev, IconRepeat, IconRepeatOne, IconShuffle } from "@/components/ui/icons";
import { useIsMobile } from "@/hooks/useIsMobile";
import { usePlayer } from "@/hooks/usePlayer";
import { sfx } from "@/lib/sfx";
import { CastButton } from "./CastButton";
import { KaraokeView } from "./KaraokeView";
import { NowPlaying } from "./NowPlaying";
import { NowPlayingSheet } from "./NowPlayingSheet";
import { ProgressBar } from "./ProgressBar";
import { QueueDrawer } from "./QueueDrawer";
import { VolumeControl } from "./VolumeControl";

export function Playbar() {
  const isMobile = useIsMobile(800);
  return isMobile ? <MobilePlaybar /> : <DesktopPlaybar />;
}

function TransportButtons({ compact = false }: { compact?: boolean }) {
  const { playing, toggle, next, prev, shuffle, setShuffle, repeat, setRepeat } = usePlayer();
  return (
    <div className="row" style={{ justifyContent: "center", gap: compact ? 10 : 14 }}>
      {!compact && (
        <button title="Aléatoire" onClick={() => { sfx.tap(); setShuffle(!shuffle); }}
                style={{ color: shuffle ? "var(--accent)" : "inherit" }}>
          <IconShuffle />
        </button>
      )}
      <button title="Précédent" onClick={() => { sfx.tap(); prev(); }}><IconPrev /></button>
      <button
        title={playing ? "Pause" : "Lecture"}
        onClick={() => { sfx.pop(); toggle(); }}
        className="btn primary"
        style={{ padding: compact ? 8 : 10, borderRadius: 999 }}
      >
        {playing ? <IconPause /> : <IconPlay />}
      </button>
      <button title="Suivant" onClick={() => { sfx.tap(); next(); }}><IconNext /></button>
      {!compact && (
        <button title={repeat === "off" ? "Activer boucle queue" : repeat === "all" ? "Boucler ce morceau" : "Désactiver"}
                onClick={() => { sfx.tap(); setRepeat(repeat === "off" ? "all" : repeat === "all" ? "one" : "off"); }}
                style={{ color: repeat !== "off" ? "var(--accent)" : "inherit", padding: 4 }}>
          {repeat === "one" ? <IconRepeatOne /> : <IconRepeat />}
        </button>
      )}
    </div>
  );
}

function DesktopPlaybar() {
  return (
    <div style={{
      height: "100%", background: "var(--bg-elev)", borderTop: "1px solid var(--line)",
      display: "grid", gridTemplateColumns: "1fr 2fr 1fr", alignItems: "center",
      padding: "0 16px", gap: 16,
    }}>
      <NowPlaying />
      <div className="stack" style={{ gap: 8, alignItems: "center" }}>
        <TransportButtons />
        {/* Cap the progress bar width so it stays tight instead of stretching
            edge-to-edge across the 2fr center column on wide displays. */}
        <div style={{ width: "100%", maxWidth: 480 }}>
          <ProgressBar />
        </div>
      </div>
      <div className="row" style={{ justifyContent: "flex-end", gap: 12 }}>
        <KaraokeView />
        <CastButton />
        <QueueDrawer />
        <VolumeControl />
      </div>
    </div>
  );
}

function MobilePlaybar() {
  const { current } = usePlayer();
  const [sheetOpen, setSheetOpen] = useState(false);
  const openSheet = () => { if (!current) return; sfx.swoosh(); setSheetOpen(true); };

  return (
    <>
      <div style={{
        height: "100%",
        display: "flex", flexDirection: "column", gap: 6,
        padding: "8px 16px",   /* extra breathing room so the trailing time stays visible */
        minWidth: 0, maxWidth: "100vw",
      }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
          gap: 8,
          minWidth: 0,
        }}>
          {/* left: current track — tap expands to full-screen NowPlayingSheet */}
          <div
            role="button"
            tabIndex={0}
            onClick={openSheet}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openSheet(); } }}
            aria-label="Ouvrir la lecture en cours"
            style={{ minWidth: 0, overflow: "hidden", cursor: current ? "pointer" : "default" }}
          >
            <NowPlaying />
          </div>
          {/* center: prev / play / next — visually locked to the horizontal midline */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <TransportButtons compact />
          </div>
          {/* right: queue drawer, right-aligned as a mirror to the left block */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
            <QueueDrawer />
          </div>
        </div>
        <ProgressBar />
      </div>
      <NowPlayingSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </>
  );
}
